import { clearRedux, clearUserInfo, getSessionData, getTokenInfo, setSessionData, setTokenInfo } from '@/auth/authUtil';
import { Message } from '@/pages/chat/components/ChatContainer';
import { Iuser, Result } from '@/typings';
import { ElLoading, ElMessage, ElMessageBox, ElNotification, isEmpty, isNotEmpty } from '@qsxy/element-plus-react';
import axios, { AxiosResponse } from 'axios';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';

const statusDesc = {
	403: '服务器拒绝执行此请求[403]',
	404: '您访问的链接丢失了[404]',
	408: '服务器等待请求已超时[408]',
	500: '系统内部发生错误[500]',
	501: '服务器不支持此请求[501]',
	504: '接口服务似乎未开启[504]',
	502: '服务器接收响应无效[502]',
	600: '系统内部发生错误[600]',
};

const http = axios.create({
	// timeout: 10000,
	withCredentials: true,
	baseURL: '/api',
});

//loading对象
let loading = null;
//当前正在请求的数量
let needLoadingRequestCount = 0;
//显示loading
function showLoading(message) {
	// 后面这个判断很重要，因为关闭时加了抖动，此时loading对象可能还存在，
	// 但needLoadingRequestCount已经变成0.避免这种情况下会重新创建个loading
	if (needLoadingRequestCount === 0 && !loading) {
		loading = ElLoading.service({
			text: message,
		});
	}
	needLoadingRequestCount++;
}
//隐藏loading
function hideLoading() {
	needLoadingRequestCount--;
	needLoadingRequestCount = Math.max(needLoadingRequestCount, 0); //做个保护
	if (needLoadingRequestCount === 0) {
		//关闭loading
		toHideLoading();
	}
}
//防抖：将 300ms 间隔内的关闭 loading 便合并为一次。防止连续请求时， loading闪烁的问题。
let toHideLoading = debounce(() => {
	loading?.close();
	loading = null;
}, 300);

function sessionTimeout() {
	clearRedux();
	clearUserInfo();
	ElMessageBox.alert('会话超时，请重新登录', '温馨提示', { type: 'warning', showClose: false }).then(() => {
		window.location.href = '/login';
	});
}

const debounceTimeout = debounce(sessionTimeout, 300);

http.interceptors.request.use(
	config => {
		const _config = {
			...config,
			// 将请求数据转换成功 formdata 接收格式
			transformRequest: config.transformRequest
				? config.transformRequest
				: data => {
						return stringify(data);
				  },
		};
		if (isEmpty(config.headers['Content-Type'])) {
			_config.headers.set('Content-Type', 'application/x-www-form-urlencoded');
		}
		_config.headers.set('Authorization', 'Bearer ' + encodeURIComponent(encodeURIComponent(getTokenInfo())));
		// 判断当前请求是否设置了不显示Loading
		// @ts-ignore
		if (Boolean(config.headers?.showLoading) === true && isNotEmpty(config.headers.message)) {
			// @ts-ignore
			showLoading(config.headers.message);
			// delete _config.headers.showLoading;
			delete _config.headers.message;
		}
		return _config;
	},
	error => {
		// 判断当前请求是否设置了不显示Loading
		if (Boolean(error.config.headers?.showLoading) === true) {
			hideLoading();
		}
		return Promise.reject(error);
	},
);

http.interceptors.response.use(
	res => {
		//判断当前请求是否设置了不显示Loading（不显示自然无需隐藏）
		// @ts-ignore
		if (Boolean(res.config.headers?.showLoading) === true) {
			hideLoading();
		}
		if (res.status === 200) {
			if (res.data?.token) {
				setTokenInfo(res.data.token);
			}
			const store = getSessionData();
			if (res.data?.user) {
				store['user'] = res.data.user;
			}
			if (res.data?.usage) {
				store['usage'] = res.data.usage;
			}
			setSessionData(store);
		}
		return res;
	},
	error => {
		//判断当前请求是否设置了不显示Loading（不显示自然无需隐藏）
		if (Boolean(error.config.headers?.showLoading) === true) {
			hideLoading();
		}
		const status = (error.response && error.response.status && error.response.status) || '';
		const data = (error.response && error.response.data) || {};
		if (status === 401) {
			// ElMessage.error({ message: '登录过期,请重新登录', grouping: true });
			debounceTimeout();
			return Promise.reject('401');
		} else if (status === 404) {
			ElMessage.error({ message: statusDesc[status], grouping: true });
			return Promise.reject('404');
		} else if (error.code == 'ECONNABORTED' && error.message.indexOf('timeout') != -1) {
			ElMessage.error({ message: '您的请求已超时（10秒），请稍后重试', grouping: true });
			return Promise.reject('timeout');
		} else if (data?.title) {
			// ElMessage(data);
			return Promise.reject(data);
		} else if (statusDesc[status]) {
			return Promise.reject(statusDesc[status]);
		}
		return Promise.reject('未知错误');
	},
);

// 添加响应拦截器
// http.interceptors.response.use(
//     // 1. http 状态码是 2xx 会进来这里，response 的数据结构如下：response-200
//     // 2. 可通过 validateStatus 配置进到 success callback 的 http status，例如:
//     //    function (status) {
//     //      return status >= 200 && status <= 500; // 当 http status 属于定义的范围内，都会进到 success callback
//     //    }
//     response => response,
//     // 1. http 状态码非2开头（没有额外定义 validateStatus）的都会进来这里，如 404, 500 等，error 的数据结构如下：error-400、error-500
//     // 2. 取消请求也会进入这里，可以用 axios.isCancel(error) 来判断是否是取消请求，error 的数据结构如下：cancel-error
//     // 3. 请求运行有异常也会进入这里，如故意将 headers 写错：axios.defaults.headers = '123'
//     // 4. 断网，error 的数据结构如下：network-error
//     error => Promise.reject(error),
// );

// 将参数转换成功 formdata 接收格式
const stringify = (data: object) => {
	const formData = new FormData();
	for (const key in data) {
		if (Object.hasOwnProperty.call(data, key)) {
			if (data[key]) {
				if (data[key].constructor === Array) {
					if (data[key][0]) {
						if (data[key][0].constructor === Object) {
							formData.append(key, JSON.stringify(data[key]));
						} else {
							data[key].forEach(item => {
								formData.append(`${key}[]`, item);
							});
						}
					} else {
						formData.append(key + '[]', '');
					}
				} else if (data[key].constructor === Object) {
					formData.append(key, JSON.stringify(data[key]));
				} else {
					formData.append(key, data[key]);
				}
			} else {
				if (data[key] === 0) {
					formData.append(key, '0');
				} else {
					formData.append(key, '');
				}
			}
		}
	}
	return formData;
};

/**
 * get请求json数据
 * @param {String} url 请求地址
 * @param {Object} params 请求参数
 * @param {Object} config 其他配置
 * @param {Function} error 报错后回调
 * @param {Boolean} override 是否覆盖报错后回调方法
 */
export function get(url: string, params?: object, callback?: (val: any) => void, error?: (msg: ErrorMessage) => void, override?: boolean) {
	http.get(url, { headers: getHeaders(params), params: getParams(params), ...getBaseUrl(params) })
		.then((result: AxiosResponse<string | object | Array<any>, any>) => {
			if (typeof result.data == 'string') {
				callback(JSON.parse(result.data));
			} else {
				callback(result.data);
			}
		})
		.catch(reason => {
			onError(error, override, isNotEmpty(reason) ? reason : null);
		});
}

/**
 * post请求json数据
 * @param {String} url 请求地址
 * @param {Object} params 请求参数
 * @param {Object} config 其他配置
 * @param {Function} error 报错后回调
 * @param {Boolean} override 是否覆盖报错后回调方法
 */
export function post(url: string, params?: object | string, callback?: (val: any) => void, error?: (msg: ErrorMessage) => void, override?: boolean) {
	http.post(url, getParams(params), {
		headers: {
			...getHeaders(params),
			'Content-Type': 'application/json;charset=UTF-8',
			Authorization: 'Bearer ' + encodeURIComponent(encodeURIComponent(getTokenInfo())),
		},
		...getBaseUrl(params),
		// 将请求数据转换成功 formdata 接收格式
		transformRequest: data => {
			return typeof data === 'string' ? data : JSON.stringify(data);
		},
	})
		.then((result: AxiosResponse<string | object | Array<any>, any>) => {
			if (typeof result.data == 'string') {
				callback(JSON.parse(result.data));
			} else {
				callback(result.data);
			}
		})
		.catch(reason => {
			onError(error, override, isNotEmpty(reason) ? reason : null);
		});
}

/**
 * post请求json数据
 * @param {String} url 请求地址
 * @param {Object} params 请求参数
 * @param {Object} config 其他配置
 * @param {Function} error 报错后回调
 * @param {Boolean} override 是否覆盖报错后回调方法
 */
export async function postStream(
	url: string,
	params?: object | string,
	onProgress?: (val: string, conversation: Message['conversation']) => void,
	onSuccess?: (val: string, conversation: Message['conversation']) => void,
	error?: (msg: ErrorMessage) => void,
	override?: boolean,
) {
	try {
		const response = await fetch('/api' + url, {
			method: 'post',
			// @ts-ignore
			responseType: 'stream',
			headers: {
				Authorization: encodeURIComponent(encodeURIComponent(getTokenInfo())),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(params),
		});
		// ok字段判断是否成功获取到数据流
		if (!response.ok) {
			const status = response.status || '';
			if (status === 401) {
				debounceTimeout();
				return Promise.reject('401');
			} else if (status === 404) {
				ElMessage.error({ message: statusDesc[status], grouping: true });
				return Promise.reject('404');
			} else if (statusDesc[status]) {
				return Promise.reject(statusDesc[status]);
			}
		}
		// 用来获取一个可读的流的读取器（Reader）以流的方式处理响应体数据
		const reader = response.body.getReader();
		// 将流中的字节数据解码为文本字符串
		const textDecoder = new TextDecoder();
		let result = true;
		const sqlValue = [''];
		while (result) {
			// done表示流是否已经完成读取  value包含读取到的数据块
			const { done, value } = await reader.read();
			if (done) {
				result = false;
				break;
			}
			// 拿到的value就是后端分段返回的数据，大多是以data:开头的字符串
			// 需要通过decode方法处理数据块，例如转换为文本或进行其他操作
			textDecoder
				.decode(value)
				.split('\n')
				.forEach(val => {
					if (!val) {
						return;
					}
					try {
						// 后端返回的流式数据一般都是以data:开头的字符，排除掉data:后就是需要的数据
						// 具体返回结构可以跟后端约定
						const text = val?.replace('data:', '') || '';

						const message = JSON.parse(text);
						if (message.event === 'message') {
							const [first, ...other] = message.answer.split('\n');
							sqlValue[sqlValue.length - 1] = sqlValue[sqlValue.length - 1] += first;
							sqlValue.push(...other);
							// console.log('sqlValue', [...sqlValue]);
							onProgress?.(sqlValue.join('\n'), { id: message.conversation_id, messageId: message.message_id });
						}
						if (message.event === 'message_end') {
							onSuccess?.(sqlValue.join('\n'), { id: message.conversation_id, messageId: message.message_id });
						}
					} catch (err) {
						//
					}
				});
		}
	} catch (reason) {
		onError(error, override, isNotEmpty(reason) ? reason : null);
	}
}

/**
 * post发送json数据
 * @param {String} url 请求地址
 * @param {Object} params 请求参数
 * @param {Object} config 其他配置
 * @param {Function} error 报错后回调
 * @param {Boolean} override 是否覆盖报错后回调方法
 */
export function postFormData(url: string, params?: object, callback?: (val: any) => void, error?: () => void, override?: boolean) {
	http.post(url, getParams(params), { headers: getHeaders(params) })
		.then((result: AxiosResponse<string | object | Array<any>, any>) => {
			if (typeof result.data == 'string') {
				callback(JSON.parse(result.data));
			} else {
				callback(result.data);
			}
		})
		.catch(reason => {
			onError(error, override, isNotEmpty(reason) ? reason : null);
		});
}

export function doImport(url: string, params?: object, callback?: (val: any) => void, error?: () => void, override?: boolean) {
	http.post(url, getParams(params), {
		headers: {
			...getHeaders(params),
			'Content-Type': 'multipart/form-data',
		},
		timeout: 0,
	})
		.then((result: AxiosResponse<string | object | Array<any>, any>) => {
			if (typeof result.data == 'string') {
				callback(JSON.parse(result.data));
			} else {
				callback(result.data);
			}
		})
		.catch(reason => {
			onError(error, override, isNotEmpty(reason) ? reason : null);
		});
}

export function doExport(url: string, params?: object, onSuccess?: (data: Blob) => void, error?: () => void, override?: boolean) {
	http.post(url, getParams(params), {
		method: 'post',
		baseURL: '/server',
		headers: {
			// 'Content-Type': 'application/x-www-form-urlencoded',
			'Content-Type': 'application/json;charset=UTF-8',
			showLoading: true,
			message: '正在导出，请稍后......',
		},
		timeout: 0,
		responseType: 'blob',
		transformRequest: data => {
			return JSON.stringify(data);
		},
	})
		.then((result: AxiosResponse<string | object | Array<any>, any>) => {
			const { data, headers } = result;
			// @ts-ignore
			onSuccess(data);
		})
		.catch(reason => {
			onError(error, override, isNotEmpty(reason) ? reason : null);
		});
}

// /**
//  * post发送json数据
//  * @param {String} url 请求地址
//  * @param {Object} params 请求参数
//  * @param {Object} config 其他配置
//  * @param {Function} error 报错后回调
//  * @param {Boolean} override 是否覆盖报错后回调方法
//  */
// export function postText(url: string, params?: string, callback?: (val: any) => void, error?: () => void, override?: boolean) {
//     reqwest({
//         url: url,
//         crossOrigin: true,
//         withCredentials: true,
//         type: 'text',
//         contentType: 'application/json',
//         method: 'post',
//         data: params,
//         success: result => {
//             callback(result.responseText);
//         },
//         error: () => {
//             onError(error, override);
//         },
//     });
// }

// /**
//  * 上传方法
//  * @param {String} url 请求地址
//  * @param {Object} params 请求参数
//  * @param {Object} config 其他配置
//  * @param {Function} error 报错后回调
//  * @param {Boolean} override 是否覆盖报错后回调方法
//  */
// export function upload(url: string, params?: object, callback?: (val: any) => void, error?: () => void, override?: boolean) {
//     reqwest({
//         url,
//         crossOrigin: true,
//         withCredentials: true,
//         type: 'json',
//         processData: false, // 使数据不做处理
//         contentType: false,
//         method: 'post',
//         data: getParams(params),
//         success: result => {
//             callback && callback(result);
//         },
//         error: () => {
//             onError(error, override);
//         },
//     });
// }

/**
 * 获取请求参数
 * @param {Object} params
 */
function getParams(params: any) {
	if (params?.headers) {
		return omit(params, 'headers');
	}
	return params;
}

/**
 * 获取请求头
 * @param {Object} params
 */
function getHeaders(params: any) {
	return params?.headers ?? {};
}

function getBaseUrl(params: any) {
	return params?.baseURL ? { baseURL: params.baseURL } : {};
}
/**
 * 错误回调
 * @param {Function} error 报错后回调
 * @param {Boolean} override 是否覆盖报错后回调方法
 */
export function onError(error: (msg: ErrorMessage) => void, override: boolean, responseText?: ErrorMessage) {
	if (error && override) {
		error(responseText);
	} else {
		if (error) {
			error(responseText);
		}
		if (isNotEmpty(responseText)) {
			if (typeof responseText !== 'string') {
				const { title, errorMsg = '' } = responseText;
				ElNotification.error({
					title: isEmpty(title?.trim()) ? '服务器响应异常' : title,
					message: errorMsg,
					duration: 0,
				});
			} else if (typeof responseText === 'string') {
				if (['timeout', '404', '401'].includes(responseText)) {
					return;
				}
				ElNotification.error({
					title: responseText,
					message: '如果您需要帮助，请联系管理员！',
					duration: 0,
				});
			}
		} else {
			ElNotification.error({
				title: '服务器响应异常',
				message: '如果您需要帮助，请联系管理员！',
				duration: 0,
			});
		}
	}
}

export function handleResult<T = any>(
	result: Result<T>,
	onSuccess: ({ message, data, token, user }: { message: string; data: T; token: string; user: Iuser }) => void,
	onErrorFn?: (data: Result<T>) => void,
) {
	const { code, message, data, token, user } = result;
	if (code === 200) {
		onSuccess?.({ message, data, token, user });
	} else if (code === 500) {
		if (onErrorFn) {
			onErrorFn(result);
		} else {
			ElMessage.error(message);
		}
	} else {
		message && ElMessage.warning(message);
	}
}

export default http;

export type ErrorMessage = string | { title?: string; errorMsg?: string };
